import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useRole } from "../../components/AppShell";
import { useConfirm, useToast } from "../../components/ui";
import { authClient } from "../../lib/auth";
import { ROLE_LABEL } from "../../lib/format";
import { useLive } from "../../lib/live";
import { chime, type NotifyPrefs, notifyPrefs, setNotifyPrefs } from "../../lib/notifications";
import { keys, useMe } from "../../lib/queries";
import { savedTheme, setTheme, type Theme } from "../../lib/theme";
import { LegalLinks } from "../legal/LegalPages";
import { Choice } from "../settings/LumiPage";

/** The signed-in person's own things: name, password, two-step sign-in and devices. */
export function AccountPage() {
  return (
    <div className="page">
      <div className="page-head">
        <h1>Mi cuenta</h1>
      </div>
      <ProfileCard />
      <AppearanceCard />
      <NotificationsCard />
      <PasswordCard />
      <TwoFactorCard />
      <DevicesCard />
      <LegalLinks />
    </div>
  );
}

function ProfileCard() {
  const me = useMe();
  const role = useRole();
  const live = useLive();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (value: string) => {
      const { error } = await authClient.updateUser({ name: value });
      if (error) throw error;
    },
    onSuccess: () => {
      setName(null);
      qc.invalidateQueries({ queryKey: keys.me });
      toast("Nombre guardado");
    },
    onError: () => toast("No se pudo guardar. Intenta de nuevo."),
  });
  if (!me.data) return null;
  const { user, tenants, activeTenantId } = me.data;
  const active = tenants.find((t) => t.tenantId === activeTenantId) ?? tenants[0];
  const value = name ?? user.name;
  const dirty = value.trim() !== user.name && value.trim().length > 0;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (dirty) save.mutate(value.trim());
  }

  return (
    <div className="card">
      <form className="card-body" onSubmit={onSubmit}>
        <div className="account-head">
          <div>
            <h3>Perfil</h3>
            <p className="muted">
              {active?.tenantName} · {role ? ROLE_LABEL[role] : ""} · {live ? "En vivo" : "Reconectando…"}
            </p>
          </div>
          <button
            className="btn"
            type="button"
            onClick={async () => {
              await authClient.signOut();
              qc.clear();
              navigate({ to: "/login" });
            }}
          >
            Cerrar sesión
          </button>
        </div>
        <div className="company-fields">
          <div className="field">
            <label htmlFor="account-name">Nombre</label>
            <input
              className="input"
              id="account-name"
              placeholder="Ej.: Ana Rodríguez"
              value={value}
              maxLength={80}
              autoComplete="name"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="account-email">Correo</label>
            <input className="input" id="account-email" value={user.email} disabled />
          </div>
        </div>
        <div>
          <button className="btn btn-primary" type="submit" disabled={!dirty || save.isPending}>
            {save.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * How this person is told about new messages and assignments on this device. Advisors
 * hear only about their own conversations; owners and admins about all of them.
 */
function NotificationsCard() {
  const role = useRole();
  const manager = role === "owner" || role === "admin";
  const [prefs, setPrefs] = useState<NotifyPrefs>(notifyPrefs);
  const [blocked, setBlocked] = useState(() => "Notification" in window && Notification.permission === "denied");
  const supported = "Notification" in window;
  const change = (patch: Partial<NotifyPrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setNotifyPrefs(next);
  };
  async function toggleBrowser() {
    if (prefs.browser) return change({ browser: false });
    const permission =
      Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
    setBlocked(permission === "denied");
    if (permission === "granted") change({ browser: true });
  }
  const rows: { key: keyof NotifyPrefs; label: string; hint: string; onClick: () => void; disabled?: boolean }[] = [
    {
      key: "cards",
      label: "Aviso en pantalla",
      hint: "Una tarjeta arriba a la derecha con el cliente y su mensaje",
      onClick: () => change({ cards: !prefs.cards }),
    },
    {
      key: "sound",
      label: "Sonido",
      hint: "Un tono corto con cada aviso",
      onClick: () => change({ sound: !prefs.sound }),
    },
    {
      key: "browser",
      label: "Aviso del navegador",
      hint: supported
        ? "Cuando Orbis está en otra pestaña o minimizado"
        : "Este navegador no permite avisos del sistema",
      onClick: toggleBrowser,
      disabled: !supported,
    },
  ];
  return (
    <div className="card">
      <div className="card-body">
        <div className="page-head">
          <div>
            <h3>Notificaciones</h3>
            <p className="muted">
              {manager
                ? "Te avisamos cuando te asignan una conversación, cuando escribe un cliente de las tuyas y cuando una conversación queda sin responsable."
                : "Te avisamos cuando te asignan una conversación y cuando escribe un cliente de las tuyas."}{" "}
              Mientras Lumi atiende, no suena. Se guarda en este dispositivo.
            </p>
          </div>
          <button className="btn btn-sm" type="button" onClick={chime}>
            Probar sonido
          </button>
        </div>
        <div className="notify-prefs">
          {rows.map((r) => (
            <div key={r.key} className="notify-pref">
              <div>
                <strong>{r.label}</strong>
                <span className="muted">{r.hint}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={prefs[r.key]}
                aria-label={r.label}
                className="switch"
                disabled={r.disabled}
                onClick={r.onClick}
              >
                <span className="switch-thumb" />
              </button>
            </div>
          ))}
        </div>
        {blocked && (
          <div className="banner banner-warn">
            El navegador tiene bloqueados los avisos de Orbis. Habilítalos en el candado de la barra de direcciones y
            vuelve a encender esta opción.
          </div>
        )}
      </div>
    </div>
  );
}

/** Light, dark or like the device; saved in this browser. */
function AppearanceCard() {
  const [theme, set] = useState<Theme>(savedTheme);
  return (
    <div className="card">
      <div className="card-body">
        <div>
          <h3>Apariencia</h3>
          <p className="muted">Solo cambia en este navegador.</p>
        </div>
        <Choice
          name="theme"
          label="Tema del panel"
          value={theme}
          onChange={(t) => {
            set(t);
            setTheme(t);
          }}
          options={[
            { value: "light", label: "Claro" },
            { value: "dark", label: "Oscuro" },
            { value: "auto", label: "Como el dispositivo", hint: "Cambia solo de día y de noche" },
          ]}
        />
      </div>
    </div>
  );
}

const MIN_PASSWORD = 12;

function PasswordCard() {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const current = String(f.get("current"));
    const next = String(f.get("next"));
    if (next.length < MIN_PASSWORD)
      return setError(`La contraseña nueva necesita al menos ${MIN_PASSWORD} caracteres.`);
    if (next !== String(f.get("repeat"))) return setError("Las dos contraseñas nuevas no coinciden.");
    setError(null);
    setBusy(true);
    // Every other device is signed out: whoever knew the old password loses access.
    const { error: failed } = await authClient.changePassword({
      currentPassword: current,
      newPassword: next,
      revokeOtherSessions: true,
    });
    setBusy(false);
    if (failed) {
      setError(
        failed.code === "INVALID_PASSWORD"
          ? "La contraseña actual no es correcta."
          : "No se pudo cambiar. Intenta de nuevo.",
      );
      return;
    }
    form.reset();
    toast("Contraseña cambiada. Se cerró la sesión en los demás dispositivos.");
  }

  return (
    <div className="card">
      <form className="card-body" onSubmit={onSubmit}>
        <div>
          <h3>Contraseña</h3>
          <p className="muted">Al cambiarla se cierra la sesión en todos tus otros dispositivos.</p>
        </div>
        <div className="company-fields">
          <div className="field">
            <label htmlFor="pw-current">Contraseña actual</label>
            <input
              className="input"
              id="pw-current"
              placeholder="Tu contraseña actual"
              name="current"
              type="password"
              autoComplete="current-password"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="pw-next">Contraseña nueva</label>
            <input
              className="input"
              id="pw-next"
              placeholder={`Mínimo ${MIN_PASSWORD} caracteres`}
              name="next"
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="pw-repeat">Repite la nueva</label>
            <input
              className="input"
              id="pw-repeat"
              placeholder="Escríbela otra vez"
              name="repeat"
              type="password"
              autoComplete="new-password"
              required
            />
          </div>
        </div>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <div>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Cambiando…" : "Cambiar contraseña"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TwoFactorCard() {
  const me = useMe();
  if (!me.data) return null;
  const on = me.data.user.twoFactorEnabled;
  return (
    <div className="card">
      <div className="card-body">
        <div className="account-head">
          <div>
            <h3>Verificación en dos pasos</h3>
            <p className="muted">
              {on
                ? "Al entrar desde un dispositivo nuevo te pedimos el código de 6 dígitos de tu app de autenticación."
                : "Todavía no está activa. Al activarla, además de la contraseña te pediremos un código de tu app de autenticación."}
            </p>
          </div>
          <span className={`pill ${on ? "pill-human" : "pill-warn"}`}>{on ? "Activa" : "Inactiva"}</span>
        </div>
      </div>
    </div>
  );
}

interface Device {
  id: string;
  token: string;
  userAgent?: string | null;
  ipAddress?: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

const when = new Intl.DateTimeFormat("es-CR", {
  day: "numeric",
  month: "long",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "America/Costa_Rica",
});

/** "Chrome en Windows" from a user agent. */
function describeDevice(ua: string | null | undefined): string {
  const s = ua ?? "";
  const browser = /Edg\//.test(s)
    ? "Edge"
    : /OPR\/|Opera/.test(s)
      ? "Opera"
      : /Firefox\//.test(s)
        ? "Firefox"
        : /Chrome\//.test(s)
          ? "Chrome"
          : /Safari\//.test(s)
            ? "Safari"
            : null;
  const os = /iPhone|iPad/.test(s)
    ? "iPhone o iPad"
    : /Android/.test(s)
      ? "Android"
      : /Windows/.test(s)
        ? "Windows"
        : /Mac OS X|Macintosh/.test(s)
          ? "Mac"
          : /Linux/.test(s)
            ? "Linux"
            : null;
  if (browser && os) return `${browser} en ${os}`;
  return browser ?? os ?? "Dispositivo desconocido";
}

/** Where the account is signed in; any other device can be signed out from here. */
function DevicesCard() {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const devices = useQuery({
    queryKey: ["account", "devices"],
    queryFn: async () => {
      const [list, current] = await Promise.all([authClient.listSessions(), authClient.getSession()]);
      const items = (list.data ?? []) as Device[];
      const mine = current.data?.session.token;
      return [...items]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .map((d) => ({ ...d, current: d.token === mine }));
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["account", "devices"] });
  const others = devices.data?.filter((d) => !d.current) ?? [];

  async function signOut(device: Device) {
    const ok = await confirm({
      title: `¿Cerrar la sesión en ${describeDevice(device.userAgent)}?`,
      message: "Ese dispositivo tendrá que volver a entrar con la contraseña.",
      confirmLabel: "Cerrar sesión",
    });
    if (!ok) return;
    const { error } = await authClient.revokeSession({ token: device.token });
    toast(error ? "No se pudo cerrar. Intenta de nuevo." : "Sesión cerrada en ese dispositivo");
    refresh();
  }

  async function signOutOthers() {
    const ok = await confirm({
      title: "¿Cerrar la sesión en los demás dispositivos?",
      message: "Solo este dispositivo queda con la sesión abierta.",
      confirmLabel: "Cerrar las demás",
    });
    if (!ok) return;
    const { error } = await authClient.revokeOtherSessions();
    toast(error ? "No se pudo cerrar. Intenta de nuevo." : "Sesiones cerradas en los demás dispositivos");
    refresh();
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="account-head">
          <div>
            <h3>Dispositivos</h3>
            <p className="muted">Dónde está abierta tu cuenta. Si no reconoces uno, ciérralo y cambia la contraseña.</p>
          </div>
          {others.length > 0 && (
            <button className="btn" type="button" onClick={signOutOthers}>
              Cerrar las demás
            </button>
          )}
        </div>
        {devices.isPending ? (
          <p className="muted">Cargando…</p>
        ) : devices.isError ? (
          <p className="muted">No se pudieron cargar los dispositivos.</p>
        ) : (
          <ul className="device-list">
            {devices.data.map((d) => (
              <li key={d.id}>
                <div>
                  <strong>{describeDevice(d.userAgent)}</strong>
                  {d.current && <span className="pill pill-human">Este dispositivo</span>}
                  <span className="muted">
                    Último uso: {when.format(new Date(d.updatedAt))}
                    {d.ipAddress ? ` · ${d.ipAddress}` : ""}
                  </span>
                </div>
                {!d.current && (
                  <button className="btn" type="button" onClick={() => signOut(d)}>
                    Cerrar sesión
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
